import transform from 'lodash/transform';
import isEqual from 'lodash/isEqual';
import isObject from 'lodash/isObject';
import API from '../config';

const objectCollide = (object, base) => {
  const changes = (target, ogObj) => transform(target, (result, value, key) => {
    if (!isEqual(value, ogObj[key])) {
      // eslint-disable-next-line no-param-reassign
      result[key] = (isObject(value) && isObject(ogObj[key])) ? changes(value, ogObj[key]) : value;
    }
  });

  return changes(object, base);
};

const boolToBit = object => {
  const newObj = {};
  Object.keys(object).map(key => {
    if (typeof object[key] === 'boolean') {
      newObj[key] = String(Number(object[key]));
    } else {
      newObj[key] = object[key];
    }
    return key;
  });
  return newObj;
};

const getImage = (url = '') => `${API.URL}image/getAwsFile?file=${url}`;

const getFileFromUrl = (url = '') => {
  const urlParts = url.split('/');
  return urlParts[urlParts.length - 1 ?? 0];
};

const sortFormDefinition = formDefinition => formDefinition.sort((a, b) => {
  const x = (a.pageSortOrder - b.pageSortOrder);
  if (x === 0) {
    return (a.SortOrder - b.SortOrder);
  }
  return x;
});

const getOrderedFormDefinition = formDefinition => {
  const sortedDef = sortFormDefinition(formDefinition);
  const flags = {};
  const unique = sortedDef.filter(item => {
    if (flags[item.PageName]) return false;
    flags[item.PageName] = true;
    return true;
  });
  return unique.map(item => ({ sectionName: item.PageName, sectionLabel: item.PageLabel }));
};

const selectSectionsPerRepeatableSection = (values = {}) => {
  const sectionCounts = {};
  const countPerSection = {};

  if (values.Rooms) {
    sectionCounts.Rooms = Object.keys(values.Rooms).map(key => values.Rooms[key].IsDeleted);
  }
  if (values.Photos) {
    sectionCounts.Photos = Object.keys(values.Photos).map(key => values.Photos[key].IsDeleted);
  }
  if (values.Supplements) {
    sectionCounts.Supplements = Object.keys(values.Supplements).map(key => values.Supplements[key].IsDeleted);
  }

  const { Photos = [], Rooms = [], Supplements = [] } = sectionCounts;

  countPerSection.Photos = Photos.filter(photo => !photo);
  countPerSection.Rooms = Rooms.filter(room => !room);
  countPerSection.Supplements = Supplements.filter(supp => !supp);
  return countPerSection;
};

export const selectErrors = validationResult => {
  const errors = [];
  Object.keys(validationResult).map(fieldName => {
    const {
      id,
      isValid,
      errorMessage = null,
    } = validationResult[fieldName];

    if (!isValid && errorMessage) (errors.push({ [id]: errorMessage }));
    return null;
  });
  return errors;
};

export const selectFieldDictionary = formDefinition => {
  const dictionary = {};
  formDefinition.map(item => {
    dictionary[item?.FieldName] = item?.PageName;
    return null;
  });
  return dictionary;
};

export const selectErrorsPerSection = (formDefinition, validationResult) => {
  const dictionary = selectFieldDictionary(formDefinition);
  const errors = selectErrors(validationResult);

  const errorsPerSection = {};
  errors.map(error => {
    const [fieldName] = Object.keys(error);
    const section = dictionary[fieldName];

    if (!errorsPerSection[section]) {
      errorsPerSection[section] = [fieldName];
    }
    // might be replace with else, includes is useless?
    if (errorsPerSection[section] && !errorsPerSection[section].includes(fieldName)) {
      errorsPerSection[section] = [...errorsPerSection[section], fieldName];
    }
    return null;
  });
  return errorsPerSection;
};

export const selectRequired = validationResult => {
  const required = [];
  Object.keys(validationResult).map(fieldName => {
    const { id, isRequired } = validationResult[fieldName];
    if (isRequired) (required.push(id));
    return null;
  });
  return required;
};

export const selectRequiredPerSection = (formDefinition, validationResult) => {
  const dictionary = selectFieldDictionary(formDefinition);
  const required = selectRequired(validationResult);
  const reqPerSection = {};
  required.map(req => {
    const section = dictionary[req];

    if (!reqPerSection[section]) (reqPerSection[section] = [req]);
    if (reqPerSection[section] && !reqPerSection[section].includes(req)) {
      reqPerSection[section] = [...reqPerSection[section], req];
    }
    return null;
  });
  return reqPerSection;
};

export const selectFormStructure = formDefinition => {
  const sortedDef = sortFormDefinition(formDefinition);
  const structure = {};
  sortedDef.map(item => {
    if (!structure[item.PageName]) {
      structure[item.PageName] = [];
    }
    structure[item.PageName] = [
      ...structure[item.PageName],
      { ...item },
    ];
    return null;
  });
  return structure;
};

export const selectOrderedFields = formDefinition => {
  const fieldOrdering = selectFormStructure(formDefinition);
  const orderedList = [];
  const baseKeys = Object.keys(fieldOrdering);

  baseKeys.map(section => {
    // sorting by field's SortOrder
    const tempResults = fieldOrdering[section].sort((a, b) => a.SortOrder - b.SortOrder);
    orderedList.push(...tempResults);
    return null;
  });
  return orderedList;
};

export const getSortedErrors = (formDefinition, validationResult) => {
  const orderedErrors = [];

  if (validationResult !== null) {
    const orderedFields = selectOrderedFields(formDefinition);
    const valKeys = validationResult.map(item => Object.keys(item)[0]);

    if (valKeys.length > 0) {
      orderedFields.map(field => {
        if (valKeys.includes(field.FieldName)) {
          let itemTitle;
          const searchableItem = validationResult.find(item => {
            [itemTitle] = Object.keys(item);
            return field.FieldName === itemTitle;
          });

          const { isValid, errorMessage } = searchableItem[itemTitle];
          if (!isValid && errorMessage) {
            orderedErrors.push({ [itemTitle]: errorMessage });
          }
        }
        return null;
      });
      if (valKeys.includes('Photos') && validationResult.Photos.errorMessage) {
        orderedErrors.push({ Photos: validationResult.Photos.errorMessage });
      }
    }
  }
  return orderedErrors;
};

export {
  objectCollide,
  boolToBit,
  getImage,
  getFileFromUrl,
  getOrderedFormDefinition,
  selectSectionsPerRepeatableSection,
};
